/* eslint-disable max-lines */
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner } from 'reactstrap';
import Time12Input from './Time12Input';
import moment from 'moment';
import {
  DailyScheduleDTO,
  DailyScheduleEmployeeDTO,
  DailyScheduleEntryDTO,
  DailyScheduleRoomDTO,
  HighlightColor,
} from '../../../../interfaces/DailyScheduleInterface';
import { HolidaysInterface } from '../../../../interfaces/HolidaysInterface';

interface WeeklyEmployeeRow {
  employeeId: string | null;
  displayName: string;
  initial: string;
  dayEntries: (DailyScheduleEntryDTO | null)[];
}

interface WeeklyRoomRow {
  roomName: string;
  highlightColor: HighlightColor;
  dayRooms: (DailyScheduleRoomDTO | null)[];
  staffCounts: number[];
  employeeRows: WeeklyEmployeeRow[];
}

interface Props {
  schedules: (DailyScheduleDTO | null)[];
  weekDates: string[];
  employees: DailyScheduleEmployeeDTO[];
  savingCell: string | null;
  approvedPtoUserIds: Record<string, string[]>;
  holidays: HolidaysInterface[];
  onUpdateEntry: (dayIndex: number, entryId: string, field: string, value: any) => Promise<void>;
  onRemoveEntry: (dayIndex: number, entryId: string) => void;
  onAddEntry: (dayIndex: number, roomName: string, employeeId: string | null) => Promise<void>;
  onAddStaffToRoom: (roomName: string, employeeId: string) => Promise<void>;
  onAddRoom: (roomName: string) => Promise<void>;
}

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const HIGHLIGHT_BG: Record<string, string> = {
  yellow: '#fffde7',
  pink: '#fce4ec',
  green: '#e8f5e9',
};

function buildGrid(schedules: (DailyScheduleDTO | null)[]): WeeklyRoomRow[] {
  const roomNames: string[] = [];
  schedules.forEach((s) => {
    (s?.rooms || []).forEach((r) => {
      if (!roomNames.includes(r.roomName)) roomNames.push(r.roomName);
    });
  });

  return roomNames.map((roomName) => {
    const dayRooms = schedules.map((s) => s?.rooms.find((r) => r.roomName === roomName) ?? null);
    const highlightColor = dayRooms.find((r) => r !== null)?.highlightColor ?? null;
    const staffCounts = dayRooms.map((r) => (r?.entries || []).length);

    const empMap = new Map<string, { name: string; initial: string; entries: (DailyScheduleEntryDTO | null)[] }>();

    dayRooms.forEach((room, dayIdx) => {
      (room?.entries || []).forEach((entry) => {
        const key = entry.employeeId ?? `__blank_${entry.id}`;
        if (!empMap.has(key)) {
          const firstName = entry.employee?.user?.firstName || '';
          const lastName = entry.employee?.user?.lastName || '';
          const name = firstName || lastName ? `${firstName} ${lastName}`.trim() : 'Unassigned';
          empMap.set(key, { name, initial: name.charAt(0).toUpperCase(), entries: [null, null, null, null, null] });
        }
        empMap.get(key)!.entries[dayIdx] = entry;
      });
    });

    const employeeRows: WeeklyEmployeeRow[] = [...empMap.entries()].map(([key, data]) => ({
      employeeId: key.startsWith('__blank_') ? null : key,
      displayName: data.name,
      initial: data.initial,
      dayEntries: data.entries,
    }));

    return { roomName, highlightColor, dayRooms, staffCounts, employeeRows };
  });
}

function TimeRangeInput({
  entry,
  dayIndex,
  highlightColor,
  savingCell,
  onUpdate,
  onRemove,
}: {
  entry: DailyScheduleEntryDTO;
  dayIndex: number;
  highlightColor: HighlightColor;
  savingCell: string | null;
  onUpdate: (dayIndex: number, entryId: string, field: string, value: any) => Promise<void>;
  onRemove: (dayIndex: number, entryId: string) => void;
}) {
  const [scheduledTime, setScheduledTime] = useState(entry.scheduledTime ?? '');
  const [scheduledEndTime, setScheduledEndTime] = useState(entry.scheduledEndTime ?? '');
  const scheduledRef = useRef(entry.scheduledTime ?? '');
  const scheduledEndRef = useRef(entry.scheduledEndTime ?? '');
  const isSaving = savingCell === `${dayIndex}-${entry.id}`;
  const bg = highlightColor ? HIGHLIGHT_BG[highlightColor] || '' : '';

  const handleScheduledBlur = () => {
    if (scheduledTime !== scheduledRef.current) {
      scheduledRef.current = scheduledTime;
      onUpdate(dayIndex, entry.id, 'scheduledTime', scheduledTime || null);
    }
  };

  const handleScheduledEndBlur = () => {
    if (scheduledEndTime !== scheduledEndRef.current) {
      scheduledEndRef.current = scheduledEndTime;
      onUpdate(dayIndex, entry.id, 'scheduledEndTime', scheduledEndTime || null);
    }
  };

  return (
    <div className="weekly-time-cell" style={{ backgroundColor: bg }}>
      {isSaving ? (
        <Spinner size="sm" style={{ width: 14, height: 14 }} />
      ) : (
        <div className="weekly-time-fields">
          <div className="weekly-time-field-row">
            <Time12Input
              value={scheduledTime}
              onChange={setScheduledTime}
              onBlur={handleScheduledBlur}
              placeholder="start"
              style={{ width: 72, fontSize: '0.75rem' }}
            />
            <span style={{ fontSize: '0.65rem', color: '#adb5bd' }}>–</span>
            <Time12Input
              value={scheduledEndTime}
              onChange={setScheduledEndTime}
              onBlur={handleScheduledEndBlur}
              placeholder="end"
              style={{ width: 72, fontSize: '0.75rem' }}
            />
          </div>
        </div>
      )}
      <button className="weekly-remove-btn" title="Remove" onClick={() => onRemove(dayIndex, entry.id)}>
        <i className="fas fa-trash" />
      </button>
    </div>
  );
}

function isHolidayDate(date: string, holidays: HolidaysInterface[]): HolidaysInterface | undefined {
  const target = moment(date, 'YYYY-MM-DD');
  const targetMonth = target.month();
  const targetDay = target.date();

  return holidays.find((h) => {
    if (!h.startDate) return false;
    const start = moment(h.startDate);
    const end = h.endDate ? moment(h.endDate) : start;

    if (h.isRecurring) {
      const startMonthDay = { month: start.month(), day: start.date() };
      const endMonthDay = { month: end.month(), day: end.date() };
      const targetMonthDay = { month: targetMonth, day: targetDay };
      return (
        (targetMonthDay.month > startMonthDay.month ||
          (targetMonthDay.month === startMonthDay.month && targetMonthDay.day >= startMonthDay.day)) &&
        (targetMonthDay.month < endMonthDay.month ||
          (targetMonthDay.month === endMonthDay.month && targetMonthDay.day <= endMonthDay.day))
      );
    }

    return target.isSameOrAfter(start, 'day') && target.isSameOrBefore(end, 'day');
  });
}

const WeeklyScheduleGrid = ({
  schedules,
  weekDates,
  employees,
  savingCell,
  approvedPtoUserIds,
  holidays,
  onUpdateEntry,
  onRemoveEntry,
  onAddEntry,
  onAddStaffToRoom,
  onAddRoom,
}: Props) => {
  const [addingStaffToRoom, setAddingStaffToRoom] = useState<string | null>(null);
  const [newStaffEmpId, setNewStaffEmpId] = useState('');
  const [addingStaff, setAddingStaff] = useState(false);
  const [addingEntryKeys, setAddingEntryKeys] = useState<Set<string>>(new Set());
  const [showAddRoomForm, setShowAddRoomForm] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [savingRoom, setSavingRoom] = useState(false);

  const today = moment().format('YYYY-MM-DD');
  const grid = buildGrid(schedules);

  const handleAddStaff = async (roomName: string) => {
    if (!newStaffEmpId) return;
    setAddingStaff(true);
    try {
      await onAddStaffToRoom(roomName, newStaffEmpId);
      setAddingStaffToRoom(null);
      setNewStaffEmpId('');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleAddRoom = async () => {
    if (!newRoomName.trim()) return;
    setSavingRoom(true);
    try {
      await onAddRoom(newRoomName.trim());
      setShowAddRoomForm(false);
      setNewRoomName('');
    } finally {
      setSavingRoom(false);
    }
  };

  const handleAddEntry = async (dayIndex: number, roomName: string, employeeId: string | null) => {
    const key = `${dayIndex}-${roomName}-${employeeId ?? '__blank__'}`;
    setAddingEntryKeys((prev) => new Set(prev).add(key));
    try {
      await onAddEntry(dayIndex, roomName, employeeId);
    } finally {
      setAddingEntryKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="weekly-grid-wrapper section-card p-0">
      <div style={{ overflowX: 'auto', paddingBottom: 0 }}>
        <table className="weekly-grid-table">
          <thead>
            <tr>
              <th className="col-left">ROOM / STAFF</th>
              {weekDates.map((date, i) => {
                const holiday = isHolidayDate(date, holidays);
                return (
                  <th key={date} className={date === today ? 'col-day col-today' : 'col-day'}>
                    <div className="day-header-label">{DAY_LABELS[i]}</div>
                    <Link
                      to={`/schedule?date=${date}`}
                      className="day-header-date day-header-link"
                      title="Open Daily Schedule"
                    >
                      {moment(date).format('M/D')}
                    </Link>
                    {holiday && (
                      <div className="weekly-holiday-badge" title={holiday.name}>
                        🎉 {holiday.name}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grid.map((roomRow) => {
              const rowBg = roomRow.highlightColor ? HIGHLIGHT_BG[roomRow.highlightColor] || '' : '';
              const usedEmpIds = new Set(roomRow.employeeRows.map((r) => r.employeeId).filter(Boolean));
              const availableEmps = employees.filter((e) => !usedEmpIds.has(e.id));

              return [
                <tr key={`room-${roomRow.roomName}`} className="room-header-row">
                  <td className="col-left room-name-cell" style={{ backgroundColor: rowBg || '#f1f3f9' }}>
                    <span className="room-name-text">{roomRow.roomName}</span>
                  </td>
                  {weekDates.map((date, i) => (
                    <td
                      key={date}
                      className={date === today ? 'col-today' : ''}
                      style={{
                        backgroundColor: rowBg || '#f1f3f9',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: '#8898aa',
                        fontWeight: 600,
                      }}
                    >
                      {roomRow.staffCounts[i] > 0 ? `${roomRow.staffCounts[i]} staff` : '—'}
                    </td>
                  ))}
                </tr>,

                ...roomRow.employeeRows.map((empRow) => (
                  <tr
                    key={`emp-${roomRow.roomName}-${empRow.employeeId ?? empRow.displayName}`}
                    className="employee-row"
                  >
                    <td className="col-left emp-name-cell">
                      <div className="emp-name-content">
                        <div className="emp-avatar">{empRow.initial}</div>
                        <span className="emp-name">{empRow.displayName}</span>
                      </div>
                    </td>
                    {weekDates.map((date, i) => {
                      const entry = empRow.dayEntries[i];
                      const room = roomRow.dayRooms[i];
                      const cellBg = roomRow.highlightColor ? HIGHLIGHT_BG[roomRow.highlightColor] || '' : '';

                      const emp = empRow.employeeId ? employees.find((e) => e.id === empRow.employeeId) : undefined;
                      const hasPto = emp && approvedPtoUserIds[date] && approvedPtoUserIds[date].includes(emp.userId);
                      const isHoliday = isHolidayDate(date, holidays);

                      if (hasPto || isHoliday) {
                        return (
                          <td
                            key={date}
                            className={date === today ? 'col-today' : ''}
                            style={{ textAlign: 'center', backgroundColor: hasPto ? '#fff3cd' : '#e8f5e9' }}
                          >
                            <span className={`weekly-block-badge ${hasPto ? 'badge-pto' : 'badge-holiday'}`}>
                              {hasPto ? 'PTO' : '🎉 Holiday'}
                            </span>
                          </td>
                        );
                      }

                      if (entry) {
                        return (
                          <td key={date} className={date === today ? 'col-today' : ''}>
                            <TimeRangeInput
                              entry={entry}
                              dayIndex={i}
                              highlightColor={roomRow.highlightColor}
                              savingCell={savingCell}
                              onUpdate={onUpdateEntry}
                              onRemove={onRemoveEntry}
                            />
                          </td>
                        );
                      }

                      const entryKey = `${i}-${roomRow.roomName}-${empRow.employeeId ?? '__blank__'}`;
                      const isAddingEntry = addingEntryKeys.has(entryKey);

                      return (
                        <td
                          key={date}
                          className={date === today ? 'col-today' : ''}
                          style={{ backgroundColor: cellBg }}
                        >
                          {isAddingEntry ? (
                            <Spinner size="sm" style={{ width: 14, height: 14 }} />
                          ) : (
                            <button
                              className="weekly-add-entry-btn"
                              onClick={() => handleAddEntry(i, roomRow.roomName, empRow.employeeId)}
                              title={room ? 'Add to this day' : 'Add room and entry for this day'}
                            >
                              +
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )),

                <tr key={`add-staff-${roomRow.roomName}`} className="add-staff-row">
                  <td className="col-left" colSpan={1}>
                    {addingStaffToRoom === roomRow.roomName ? (
                      <div className="add-staff-form">
                        <select
                          className="weekly-emp-select"
                          value={newStaffEmpId}
                          onChange={(e) => setNewStaffEmpId(e.target.value)}
                        >
                          <option value="">Select employee...</option>
                          {availableEmps.map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.user.firstName} {e.user.lastName}
                            </option>
                          ))}
                        </select>
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!newStaffEmpId || addingStaff}
                          onClick={() => handleAddStaff(roomRow.roomName)}
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          {addingStaff ? <Spinner size="sm" style={{ width: 10, height: 10 }} /> : 'Add'}
                        </Button>
                        <button
                          className="weekly-remove-btn"
                          onClick={() => {
                            setAddingStaffToRoom(null);
                            setNewStaffEmpId('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button className="weekly-add-staff-btn" onClick={() => setAddingStaffToRoom(roomRow.roomName)}>
                        + Add staff to {roomRow.roomName}
                      </button>
                    )}
                  </td>
                  {weekDates.map((date) => (
                    <td key={date} />
                  ))}
                </tr>,
              ];
            })}

            {grid.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4" style={{ fontSize: '0.85rem' }}>
                  No rooms scheduled for this week.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="p-3">
        {showAddRoomForm ? (
          <div className="add-staff-form">
            <input
              className="weekly-emp-select"
              style={{ width: 200 }}
              placeholder="Room name..."
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRoom()}
              autoFocus
            />
            <Button
              color="primary"
              size="sm"
              disabled={!newRoomName.trim() || savingRoom}
              onClick={handleAddRoom}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
            >
              {savingRoom ? <Spinner size="sm" style={{ width: 10, height: 10 }} /> : 'Add'}
            </Button>
            <button
              className="weekly-remove-btn"
              onClick={() => {
                setShowAddRoomForm(false);
                setNewRoomName('');
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <Button color="primary" outline size="sm" onClick={() => setShowAddRoomForm(true)}>
            + Add Room
          </Button>
        )}
      </div>
    </div>
  );
};

export default WeeklyScheduleGrid;
